import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import EmailPillField from '../components/contract/EmailPillField';
import Banner from '../components/ui/Banner';
import { SaveIcon, UploadIcon, EyeIcon, DownloadIcon } from '../ui/icons';
import SimpleSelect from '../ui/SimpleSelect';
import { useConfirm } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import PaperSheet, { pageCountLabel } from '../components/document/PaperSheet';
import { OFFER_TABLE_COLUMNS, OFFER_TABLE_CELL_ALIGN } from '../utils/offerTableColumns';
import { getMissingCompanyFields } from '../utils/companyData';
import {
  StatusStrip, StatusText, HistoryBox, HistoryHead, VersionCard, VersionDate, SheetNote,
  PublishButton, SmallButton, ModalOverlay, ModalBox, ModalTitle, ModalText, ModalActions,
  PdfModalBox, PdfModalHead, PdfDownloadLink, PdfFrame, PdfCloseButton,
  Container, TopBar, TitleBlock, PageTitle, TopActions, Intro, Notices,
  MainGrid, Panel, PreviewPanel, PanelHead, PanelTitle, PanelTools, PreviewFrame,
} from '../components/document/builderChrome';
import { zarejestrujZapisPrzedWyjsciem, trwaWylogowanie, kontoWKarcie } from '../utils/zapisPrzedWyjsciem';
import {
  Badge,
  ToolbarButton as Button,
  VariableChip,
  LinkButton,
} from '../ui/common';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

// O.4: Konfigurator oferty. Edytujemy tylko "chrome" (logo, czcionka, tytuł, wstęp,
// stopka, ważność) zapisywane w contract_settings.offer_template (JSONB). Trzon oferty
// (tabele danych/sprzętu/podsumowania) jest automatyczny — tu pokazujemy go w podglądzie
// na przykładowych danych. Zapis wysyła WYŁĄCZNIE pola konstruktora oferty (PUT zapisuje tylko
// pola obecne w payloadzie, 11.10.2026), więc nie rusza danych firmy.
//
// REDESIGN 2026-08-21: strona dostała ten sam układ co Konstruktor umowy
// (`pages/ContractTemplateBuilder.js`) — przyklejony pasek akcji u góry, podgląd jako
// kartka towarzysząca przewijaniu po prawej, wspólne prymitywy z `ui/common.js`.
// Obie strony są dla klienta tym samym zadaniem („ułóż dokument, zobacz jak wyjdzie"),
// więc nie mogą wyglądać jak dwa różne produkty.

// P8.1e (5/6): te wartości ZOSTAJĄ PO POLSKU. To domyślna TREŚĆ dokumentu, nie napis
// interfejsu — ląduje w `tpl`, zapisuje się jako `contract_settings.offer_template`
// i stamtąd wchodzi do PDF-a, który dostaje KLIENT wypożyczalni. Przetłumaczona
// zmieniałaby się razem z językiem PANELU, więc niemieckojęzyczny pracownik opublikowałby
// niemiecki szablon oferty dla polskich klientów (ten sam powód co `DEFAULTS`
// w `pages/Notifications.js`).
const OFFER_TPL_DEFAULTS = {
  title: 'OFERTA WYPOŻYCZENIA SPRZĘTU',
  logoEnabled: false,
  logoAlign: 'center',
  fontPt: 11,
  introText: '',
  footerText: 'W celu potwierdzenia rezerwacji prosimy o wpłatę powyższej zaliczki. W tytule przelewu prosimy wpisać imię nazwisko oraz nr oferty.',
  validityDays: 14,
};

// Zmienne (pigułki) dostępne w tekstach wstępu/stopki: kod → KLUCZ i18n etykiety.
// P8.1e (5/6): bezpieczne, bo klik wstawia do tekstu KOD przez insertVar(name) —
// etykieta żyje wyłącznie na pigułce i nie trafia do treści oferty.
const OFFER_VAR_KEYS = {
  klient: 'offerTemplate.varKlient',
  email: 'offerTemplate.varEmail',
  nrOferty: 'offerTemplate.varNrOferty',
  dataOferty: 'offerTemplate.varDataOferty',
  firma: 'offerTemplate.varFirma',
  bank: 'offerTemplate.varBank',
  nrKonta: 'offerTemplate.varNrKonta',
  terminOd: 'offerTemplate.varTerminOd',
  terminDo: 'offerTemplate.varTerminDo',
  razem: 'offerTemplate.varRazem',
  zaliczka: 'offerTemplate.varZaliczka',
  depozyt: 'offerTemplate.varDepozyt',
  waznosc: 'offerTemplate.varWaznosc',
};

// Przykładowe wartości do podglądu (zastępują pigułki).
const SAMPLE = (tpl, settings) => ({
  klient: 'Jan Kowalski',
  email: 'jan.kowalski@example.com',
  nrOferty: 'OF/2026/001',
  dataOferty: new Date().toLocaleDateString('pl-PL'),
  // Bez danych firmy kartka pokazuje to samo co PDF (lib/offerHtml.js), czyli „-”, a nie
  // wymyśloną firmę, bank i numer konta (test odbiorczy 11.10.2026).
  firma: settings.company_name || '-',
  bank: settings.company_bank_name || '-',
  nrKonta: settings.company_bank_account || '-',
  // Same daty, jak w prawdziwej ofercie (rezerwacje nie mają godzin, test odbiorczy 11.10.2026).
  terminOd: '10.01.2026',
  terminDo: '14.01.2026',
  // `cena`/`rabat` nie maja pigulki w OFFER_VARS — sluza WYLACZNIE podgladowi, zeby blok
  // podsumowania mial te same piec wierszy co wygenerowany PDF (530 - 50 = 480).
  // Kwoty z kropką, jak w PDF (generator drukuje toFixed(2)).
  cena: '530.00',
  rabat: '50.00',
  razem: '480.00',
  zaliczka: '150.00',
  depozyt: '300.00',
  waznosc: String(tpl.validityDays || 14),
});

// Licznik znaków wstępu i stopki: serwer odrzuca ponad 2000 (dawniej po cichu ucinał).
const LIMIT_TEKSTU_OFERTY = 2000;
function LicznikZnakow({ tekst, dataCy }) {
  const n = String(tekst || '').trim().length;
  return (
    <Hint data-cy={dataCy} style={n > LIMIT_TEKSTU_OFERTY ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
      {n}/{LIMIT_TEKSTU_OFERTY}
    </Hint>
  );
}

// Ta sama reguła co serwer (Login/backend/lib/offerTemplateLimits.js): wstęp i stopka po trim()
// najwyżej 2000 znaków. Panel sprawdza ją PRZED wysłaniem, żeby zapis przy wyjściu ze strony,
// „Zapisz szkic”, okno publikacji i podgląd PDF nie obiecywały czegoś, co serwer odrzuci (test
// odbiorczy 11.10.2026: okno publikacji mówiło „Brak ostrzeżeń”, a publikacja kończyła się 400).
export const zaDlugiTekstOferty = (szkic) => ['introText', 'footerText']
  .some((k) => typeof szkic?.[k] === 'string' && szkic[k].trim().length > LIMIT_TEKSTU_OFERTY);

// Szkic, którego nie udało się zapisać przy wyjściu ze strony (odmowa serwera, brak sieci,
// wygasła sesja), czeka w sessionStorage tej karty i wraca przy następnym wejściu do konstruktora.
// Dawniej błąd tego zapisu był połykany, a zmiany znikały razem ze stroną (test odbiorczy
// 11.10.2026). Numer konta w kluczu: po przelogowaniu w tej samej karcie nie wraca cudzy szkic.
export const kluczSzkicuOferty = () => {
  let id = null;
  try { id = localStorage.getItem('userId'); } catch (_) { id = null; }
  return `offerTemplateUnsaved:v1:${id || 'anon'}`;
};
const zachowajSzkicLokalnie = (szkic) => {
  try { sessionStorage.setItem(kluczSzkicuOferty(), JSON.stringify({ draft: szkic })); } catch (_) { /* tryb prywatny, pełna pamięć */ }
};
const usunSzkicLokalny = () => {
  try { sessionStorage.removeItem(kluczSzkicuOferty()); } catch (_) { /* jw. */ }
};
const odczytajSzkicLokalny = () => {
  try {
    const wpis = JSON.parse(sessionStorage.getItem(kluczSzkicuOferty()) || 'null');
    if (!wpis || !wpis.draft || typeof wpis.draft !== 'object') return null;
    // Tylko pola szablonu, w kolejności wartości domyślnych (porównujemy przez JSON).
    const szkic = { ...OFFER_TPL_DEFAULTS };
    Object.keys(OFFER_TPL_DEFAULTS).forEach((k) => { if (k in wpis.draft) szkic[k] = wpis.draft[k]; });
    return szkic;
  } catch (_) {
    return null;
  }
};

// Dane firmy, które oferta DRUKUJE: nazwa (blok „Dane do przelewu” i stopka PDF), bank i numer
// konta. Bez nich oferta ma w tych miejscach „-”, a konstruktor milczał (test odbiorczy
// 11.10.2026). „Wypełnione” jak w konstruktorze umowy (utils/companyData.js); NIP i adresu oferta
// nie drukuje, więc ich brak nie jest tu ostrzeżeniem. Zwraca brakujące pola z kluczem etykiety.
export const brakiDanychFirmyOferty = (s) => {
  const braki = [];
  if (getMissingCompanyFields(s).some((m) => m.key === 'company_name')) {
    braki.push({ key: 'company_name', labelKey: 'offerTemplate.missingName' });
  }
  if (!String((s && s.company_bank_name) || '').trim()) {
    braki.push({ key: 'company_bank_name', labelKey: 'offerTemplate.missingBankName' });
  }
  if (!String((s && s.company_bank_account) || '').trim()) {
    braki.push({ key: 'company_bank_account', labelKey: 'offerTemplate.missingAccount' });
  }
  return braki;
};

// Lustro lib/offerHtml.js#waznaDo dla podglądu: dziś plus liczba dni, „DD.MM.RRRR”.
const waznaDoPodglad = (dni) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + (Number(dni) || 14));
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// Liczba dni ważności oferty z pola tekstowego: całkowita 1-365 (te same granice co serwer),
// liczba ponad 365 przycięta do 365, wszystko inne (puste, 0, ujemne, tekst) = brak wartości.
export const poprawnaWaznosc = (tekst) => {
  const t = String(tekst ?? '').trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 ? Math.min(365, n) : null;
};

// Wiersze tabeli sprzętu na kartce: te same dane co „Podgląd PDF” (routes/offers.js,
// POST /api/preview-offer-pdf), kwoty z kropką, bo generator drukuje toFixed(2). Dawniej kartka
// miała „240,00 zł” (test odbiorczy 11.10.2026).
const PRZYKLADOWE_WIERSZE = [
  ['1', 'Narty', 'sport', 'Atomic', '170', '1', '60.00 zł', '4', '240.00 zł'],
  ['2', 'Buty', '-', 'Salomon', '42', '1', '30.00 zł', '4', '120.00 zł'],
];

// Pole ważności w trakcie edycji z niepoprawnym tekstem (puste, 0, ułamek): do zapisu idzie
// liczba sprzed edycji, nie wartość pośrednia z kasowania (test odbiorczy 11.10.2026).
export const zWaznosciaPrzed = (szkic, tekst, przed) => (
  tekst !== null && tekst !== undefined && poprawnaWaznosc(tekst) === null && przed != null
    ? { ...szkic, validityDays: przed }
    : szkic
);

const renderSampleText = (text, vals) =>
  String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k) => (vals[k] != null ? vals[k] : ''));

// ── STYLED ──────────────────────────────────────────────────────────────────
// Wszystko na tokenach z index.css. Jedyne kolory wpisane wprost siedzą w podglądzie
// niżej i to jest celowe: kartka oferty ma wyglądać jak wydruk w KAŻDYM motywie.

/* Grupa pol w panelu edycji — odpowiednik karty sekcji w konstruktorze umowy. */
const Group = styled.div`
  padding: var(--sp-4);
  margin-bottom: var(--sp-3);
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  transition: border-color var(--dur) var(--ease);

  &:last-of-type { margin-bottom: 0; }
  &:focus-within { border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

const GroupTitle = styled.h3`
  margin: 0 0 var(--sp-3) 0;
  font-size: var(--fs-base);
  font-weight: 700;
  color: var(--text);
`;

const Field = styled.div`
  margin-bottom: var(--sp-4);

  &:last-child { margin-bottom: 0; }
`;

const Label = styled.label`
  display: block;
  margin-bottom: var(--sp-2);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  font-family: inherit;
  font-size: var(--fs-base);
  background: var(--input-bg);
  color: var(--text);

  &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
`;

/* Ramka fokusu klawiatury na polu wyboru: globalne `input:focus { outline: none }` z index.css
   wygrywa ze wspólną siatką `:focus-visible` (wyższa specyficzność), a natywne pole wyboru nie
   pokazuje koloru obramowania, więc Tab na „Pokaż logo” nie zostawiał żadnego śladu (test
   odbiorczy 11.10.2026). */
const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-base);
  color: var(--text);
  cursor: pointer;

  input { width: 16px; height: 16px; accent-color: var(--primary); }
  input:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const Hint = styled.p`
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-xs);
  color: var(--text-muted);
`;

const Row = styled.div`
  display: flex;
  gap: var(--sp-4);
  flex-wrap: wrap;
  align-items: center;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
`;

/* Kartka oferty — bialy arkusz z czarnym pismem NIEZALEZNIE od motywu panelu, bo tak
   wyjdzie z drukarki; ten sam wzorzec co podglad umowy i PdfFrame w InvoicePreview.js.

   WARTOSCI NIZEJ SA LUSTREM `Login/backend/lib/offerHtml.js`. Podglad ma pokazywac dokument,
   ktory dostanie klient, a nie jego przyblizenie — jesli tam zmienia sie odstep, ramka albo
   szarosc naglowka, tutaj tez. */
/* TYPOGRAFIA dokumentu wewnątrz kartki. Samą kartkę (wymiary A4, marginesy druku,
   skalowanie do szerokości kolumny, linie podziału na strony) rysuje
   `components/document/PaperSheet.js` — tło, cień i padding nie należą już tutaj,
   bo muszą odpowiadać milimetrom papieru, a nie szerokości panelu. */
const Paper = styled.div`
  color: #1a1a1a;
  font-family: Calibri, 'Segoe UI', Arial, sans-serif;
  font-size: ${p => p.$fontPt || 11}pt;
  line-height: 1.5;
`;


const PLogo = styled.div`
  margin-bottom: 14px;
  text-align: ${p => p.$align || 'center'};
  img { max-height: 60px; max-width: 240px; object-fit: contain; }
`;

const PTitle = styled.div`
  text-align: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 1.45em;
  margin: 0 0 10px;
`;

const PTitleRule = styled.div`
  border-bottom: 1.5px solid #1a1a1a;
  margin: 0 0 18px;
`;

/* `pre-line`, nie `pre-wrap`: PDF zamienia nowe linie na <br>, a kilka spacji z rzędu zwija
   do jednej jak każdy HTML, więc kartka z `pre-wrap` pokazywała szerokie odstępy, których
   w ofercie nie ma (test odbiorczy 11.10.2026). */
const PIntro = styled.div`
  margin: 0 0 16px;
  line-height: 1.55;
  white-space: pre-line;
`;

/**
 * UWAGA — kolory tabel podgladu MUSZA byc wpisane wprost.
 *
 * index.css ma domyslne reguly dla KAZDEJ tabeli w aplikacji: `table { color: var(--text) }`,
 * `table tbody td { color: var(--text) }`, `table thead th { background: var(--table-header-bg) }`
 * oraz podswietlenie wiersza pod kursorem. Na bialej kartce oferty w trybie ciemnym dawalo to
 * jasnoszare pismo na bialym tle (tresc tabel byla praktycznie niewidoczna) i granatowy wiersz
 * naglowka sprzetu. Kartka ma wygladac jak wydruk w obu motywach, wiec przejmuje kolor u siebie.
 */
/* `font-size: inherit` w komórkach: index.css daje KAŻDEJ komórce tabeli w panelu stały rozmiar
   `var(--fs-base)`, więc tabele kartki nie reagowały na „Wielkość tekstu”, kwoty łamały się na
   dwie linie, a podpis liczył za dużo stron (test odbiorczy 11.10.2026). W PDF komórki dziedziczą
   rozmiar dokumentu. Podświetlenie wiersza pod kursorem też jest regułą panelu: na wydruku go
   nie ma, a dawne `td { background: inherit }` przejmowało je zamiast wyłączać. */
const PTableBase = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #1a1a1a;
  font-variant-numeric: tabular-nums;
  td, th { border: 1px solid #cfcfcf; padding: 7px 10px; color: #1a1a1a; vertical-align: middle; font-size: inherit; }
  tbody tr:hover { background: transparent; }
`;

/* Dane oferty: etykieta na szarym tle, wartosc na bialym. Wczesniej OBIE komorki mialy
   to samo szare tlo, wiec wiersz czytalo sie jak jedno pole bez odpowiedzi. */
const PInfoTable = styled(PTableBase)`
  margin: 0 0 12px;
  .label { background: #f2f2f2; font-weight: 700; letter-spacing: 0.02em; width: 38%; }
  .val { background: #fff; }
`;

const PDateRow = styled(PTableBase)`
  margin: 0 0 14px;
  text-align: center;
  .caption { background: #f2f2f2; font-weight: 700; letter-spacing: 0.02em; text-align: left; }
  .when { color: #5b5b5b; font-size: 0.85em; }
  .stamp { font-weight: 700; font-size: 1.05em; white-space: nowrap; }
`;

/* Wyrównanie komórek wierszy idzie z OFFER_TABLE_CELL_ALIGN (inline, jak w PDF), nie z reguły
   „wszystko do środka”. Pismo najwyżej 10 pt i węższe odstępy: przy 13-14 pt tabela była szersza
   od strony A4 (lustro `.equipment-table` w lib/offerHtml.js, test odbiorczy 11.10.2026). */
const PEquip = styled(PTableBase)`
  margin: 14px 0 18px;
  font-size: min(0.84em, 10pt);
  th { background: #f2f2f2; font-weight: 700; text-align: center; letter-spacing: 0.02em; border-bottom: 1.5px solid #9a9a9a; }
  th, td { padding: 6px 6px; }
  tbody tr:nth-child(even) td { background: #fafafa; }
`;

/* Podsumowanie jak blok koncowy faktury: waskie, dosuniete do prawej, kwoty w slupku. */
const PSummary = styled(PTableBase)`
  width: 58%;
  margin: 0 0 0 auto;
  .label { text-align: left; font-weight: 600; width: 58%; }
  .val { text-align: right; white-space: nowrap; font-weight: 600; }
  .total td { background: #f2f2f2; font-size: 1.06em; font-weight: 700; border-top: 1.5px solid #9a9a9a; border-bottom: 1.5px solid #9a9a9a; }
`;

const PPayBox = styled.div`
  margin-top: 20px;
  border: 1px solid #cfcfcf;
  background: #fafafa;
  padding: 12px 14px;
  font-size: 0.92em;
  line-height: 1.6;

  .pay-title { font-weight: 700; letter-spacing: 0.02em; margin-bottom: 4px; }
`;

const PFooter = styled.div`
  margin-top: 14px;
  font-size: 0.92em;
  line-height: 1.6;
  white-space: pre-line;
`;

const PValidity = styled.div`
  margin-top: 14px;
  font-size: 0.85em;
  color: #5b5b5b;
`;

function OfferTemplate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  // Komunikat ulotny przeżywa zmianę trasy (ToastProvider owija całą aplikację), więc tylko nim
  // da się powiedzieć o nieudanym zapisie przy wyjściu ze strony.
  const toast = useToast();
  // Edytor dostał szkic, którego nie udało się zapisać przy ostatnim wyjściu (sessionStorage).
  const [przywrocono, setPrzywrocono] = useState(false);
  // GET /api/contract-settings się nie udał: zapis zablokowany, baner z odświeżeniem strony.
  const [odczytNieudany, setOdczytNieudany] = useState(false);
  // Pole „Oferta ważna przez (dni)” trzyma SUROWY tekst; do szablonu trafia wyłącznie poprawna
  // liczba 1-365. Dawniej wyczyszczenie pola wstawiało od razu 14, więc wpisanie „7” dawało 147
  // (test odbiorczy 11.10.2026).
  const [waznoscTekst, setWaznoscTekst] = useState(null);
  // Liczba dni z chwili wejścia w pole. W trakcie pisania podgląd dostaje każdą poprawną liczbę,
  // także pośrednią: kasowanie „14” przechodzi przez „1”. Gdy po wyjściu z pola tekst jest
  // niepoprawny (puste, 0, ułamek), wraca ta wartość, a nie pozostałość z kasowania, czyli
  // nigdy przypadkowy 1 dzień (test odbiorczy 11.10.2026).
  const waznoscPrzed = useRef(null);

  // CYKL ŻYCIA SZABLONU (2026-08-21, migracja 041) — taki sam jak w konstruktorze umowy:
  //   `tpl`          — to, co właśnie edytujesz (szkic),
  //   `publishedTpl` — wersja, z której generują się PRAWDZIWE oferty (`offer_template`),
  //   `versions`     — poprzednie wersje opublikowane, do przywrócenia.
  // Do tej pory istniał tylko jeden przycisk „Zapisz", który natychmiast zmieniał dokument
  // wysyłany klientom; nie było gdzie odłożyć niedokończonej zmiany ani do czego wrócić.
  const [settings, setSettings] = useState(null); // ustawienia z GET; null = nie wczytano
  const [tpl, setTpl] = useState(OFFER_TPL_DEFAULTS);
  const [publishedTpl, setPublishedTpl] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  // 'save-draft' | 'publish' | 'view-pdf' | 'download' — jak w konstruktorze umowy:
  // jeden stan blokuje wszystkie akcje paska, żeby nie dało się ich uruchomić równolegle.
  const [actionLoading, setActionLoading] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  // Błąd publikacji pokazywany W OKNIE (jak w konstruktorze umowy): okno czeka na odpowiedź
  // serwera, a baner u góry strony bywa poza ekranem (scalenie poprawek 11.10.2026).
  const [publishError, setPublishError] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  // Po zamknięciu podglądu PDF fokus wraca na „Podgląd PDF” (sprawdzenie 12.10.2026).
  const pdfPrzyciskRef = useRef(null);
  const activeEditor = useRef(null);
  // Migawka ostatnio ZAPISANEGO szkicu — bez niej nic nie mówiło, że wyjście ze strony
  // po edycji po cichu porzuca zmiany.
  const savedDraft = useRef(null);
  // `settings` w ref, żeby zapis nie zależał od tego, który render go zawołał.
  const settingsRef = useRef(null);
  // Liczba stron A4 policzona przez PaperSheet z wysokości treści — tylko do podpisu
  // pod kartką (dokładny podział zna dopiero render PDF).
  const [previewPages, setPreviewPages] = useState(1);

  useEffect(() => { settingsRef.current = settings; }, [settings]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/contract-settings`, { credentials: 'include' });
        // Bez udanego odczytu NIE ustawiamy `settings`: putSettings odmawia wtedy zapisu
        // („settingsNotLoaded”), zamiast zapisać szkic na wzorze domyślnym.
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        const s = data.settings || {};
        setSettings(s);

        const rawPublished = (s.offer_template && typeof s.offer_template === 'object') ? s.offer_template : null;
        let published = null;
        if (rawPublished) {
          published = { ...OFFER_TPL_DEFAULTS, ...rawPublished };
          // Zgodność wstecz: przed konfiguratorem tytuł mieszkał w osobnej kolumnie.
          if (!String(rawPublished.title || '').trim() && String(s.offer_title || '').trim()) {
            published.title = s.offer_title;
          }
        }
        setPublishedTpl(published);

        const state = (s.offer_template_state && typeof s.offer_template_state === 'object')
          ? s.offer_template_state
          : {};
        setVersions(Array.isArray(state.versions) ? state.versions : []);

        const draft = (state.draft && typeof state.draft === 'object')
          ? { ...OFFER_TPL_DEFAULTS, ...state.draft }
          : null;
        const bazowy = draft || published || { ...OFFER_TPL_DEFAULTS };
        // Szkic, którego nie udało się zapisać przy ostatnim wyjściu, wraca do edytora jako
        // niezapisane zmiany (baner niżej). Gdy serwer ma już to samo (zapis jednak doszedł),
        // wpis znika bez komunikatu.
        const lokalny = odczytajSzkicLokalny();
        if (lokalny && JSON.stringify(lokalny) !== JSON.stringify(bazowy)) {
          setTpl(lokalny);
          setPrzywrocono(true);
        } else {
          if (lokalny) usunSzkicLokalny();
          setTpl(bazowy);
        }
        savedDraft.current = draft ? JSON.stringify(draft) : null;
      } catch (e) {
        if (alive) setOdczytNieudany(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const tplJson = useMemo(() => JSON.stringify(tpl), [tpl]);
  const publishedJson = useMemo(() => (publishedTpl ? JSON.stringify(publishedTpl) : null), [publishedTpl]);

  // Punkt odniesienia dla „Niezapisane zmiany": zapisany szkic, a jak go nie ma —
  // wersja opublikowana, a jak i jej nie ma — wartości domyślne. Bez tego ostatniego
  // przypadku organizacja, która niczego jeszcze nie opublikowała, mogła edytować
  // szablon i nie dostać ŻADNEGO sygnału, że zmiany nigdzie nie trafiły.
  const baselineJson = savedDraft.current !== null
    ? savedDraft.current
    : (publishedJson !== null ? publishedJson : JSON.stringify(OFFER_TPL_DEFAULTS));
  const dirty = tplJson !== baselineJson;

  const hasSavedDraft = savedDraft.current !== null;
  const templateStatus = (publishedTpl && !hasSavedDraft && tplJson === publishedJson)
    ? 'published'
    : ((!publishedTpl && !hasSavedDraft && !dirty) ? 'empty' : 'draft');

  const setField = (k, v) => setTpl(t => ({ ...t, [k]: v }));

  const insertVar = (name) => {
    const ed = activeEditor.current;
    if (!ed) {
      setAlert({ ok: false, msg: t('offerTemplate.clickFieldFirst') });
      return;
    }
    ed.chain().focus().insertContent({ type: 'variablePill', attrs: { name } }).run();
  };

  /**
   * PUT /api/contract-settings zapisuje tylko pola obecne w payloadzie, więc wysyłamy
   * WYŁĄCZNIE to, co zmienia konstruktor oferty. Dawniej round-trip całego `settings`
   * z chwili otwarcia strony cofał zmiany zrobione w międzyczasie w „Danych firmy”
   * (np. numer konta), a po nieudanym odczycie ustawień kasował wszystkie dane firmy
   * (test odbiorczy 11.10.2026).
   */
  const putSettings = async (patch) => {
    const current = settingsRef.current;
    if (!current) throw new Error(t('offerTemplate.settingsNotLoaded'));
    const csrf = localStorage.getItem('csrfToken') || '';
    const res = await fetch(`${API_BASE}/api/contract-settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      // Wygasła sesja (401, a po wylogowaniu w innej karcie 403 csrf_invalid) dostaje zdanie
      // zamiast surowego kodu z serwera, jak w konstruktorze umowy (test odbiorczy 11.10.2026).
      if (res.status === 401 || (res.status === 403 && err?.error === 'csrf_invalid')) {
        throw new Error(t('common.sessionExpired'));
      }
      throw new Error(err?.error || t('offerTemplate.saveError'));
    }
    setSettings(s => ({ ...(s || {}), ...patch }));
  };

  // Zapis się udał: szkic odłożony w tej karcie po nieudanym wyjściu przestaje być potrzebny.
  const zapisanoNaSerwerze = () => {
    usunSzkicLokalny();
    setPrzywrocono(false);
  };

  // „Odrzuć zmiany” przy przywróconym szkicu: edytor wraca do tego, co jest na serwerze.
  const odrzucPrzywrocone = () => {
    usunSzkicLokalny();
    setPrzywrocono(false);
    setTpl(savedDraft.current ? JSON.parse(savedDraft.current) : (publishedTpl || { ...OFFER_TPL_DEFAULTS }));
  };

  const handleSaveDraft = async () => {
    if (zaDlugiTekstOferty(tpl)) {
      setAlert({ ok: false, msg: t('offerTemplate.textTooLong') });
      return;
    }
    setActionLoading('save-draft');
    setAlert(null);
    try {
      // Sam szkic: historię wersji zmieniają wyłącznie publikacja i usunięcie wersji, więc karta
      // otwarta przed publikacją w innej karcie nie cofa historii (przegląd kodu 12.10.2026).
      await putSettings({ offer_template_state: { draft: tpl } });
      savedDraft.current = tplJson;
      zapisanoNaSerwerze();
      setAlert({ ok: true, msg: t('offerTemplate.draftSaved') });
    } catch (e) {
      setAlert({ ok: false, msg: e.message || t('offerTemplate.saveError') });
    } finally {
      setActionLoading(null);
    }
  };

  // Wyjście ze strony z niezapisanymi zmianami (test odbiorczy 11.10.2026): zmiany przepadały
  // bez ostrzeżenia. Przejście w panelu zapisuje je jako szkic (klienci dalej dostają wersję
  // opublikowaną), a zamknięcie karty albo przeładowanie pyta przeglądarka. Dawny powód braku
  // autozapisu (zapis całego wiersza ustawień razem z logo) zniknął, bo PUT zapisuje tylko
  // przysłane pola.
  //
  // Ponowny test 11.10.2026: gdy ten zapis się nie udał (wstęp ponad 2000 znaków, brak sieci,
  // wygasła sesja), błąd był połykany i zmiany znikały bez śladu. Panel nie ma routera danych,
  // więc przejścia nie da się zatrzymać; zamiast tego szkic trafia NAJPIERW do sessionStorage tej
  // karty (znika dopiero po udanym zapisie), a porażkę ogłasza komunikat z powrotem do
  // konstruktora, gdzie szkic wraca do edytora. Tekst za długi nie jest w ogóle wysyłany.
  const wyjscieRef = useRef({});
  wyjscieRef.current = { dirty, tpl, putSettings, baselineJson, waznoscTekst, toast, t, navigate };
  useEffect(() => {
    // Zapis przy wyjściu i przed wylogowaniem tylko dla konta z chwili otwarcia strony: po
    // zalogowaniu na inne konto w innej karcie poszedłby z jego sesją, czyli do jego firmy
    // (przegląd bezpieczeństwa 12.10.2026).
    const kontoPrzyWejsciu = kontoWKarcie();
    const ostrzez = (e) => {
      // Po wylogowaniu bez pytania o wyjście (sprawdzenie 12.10.2026, jak w konstruktorze umowy).
      if (trwaWylogowanie() || !wyjscieRef.current.dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ostrzez);
    // Wylogowanie (utils/wyloguj.js) zapisuje szkic, póki sesja jeszcze istnieje: pełne
    // przeładowanie na /login nie odmontowuje strony, więc sprzątanie niżej się nie wykona.
    // Niezapisany szkic czeka najpierw w tej karcie (jak przy wyjściu), a znika po udanym
    // zapisie; komunikatu nie ma, bo strona zaraz się przeładuje (scalenie poprawek 11.10.2026).
    const wyrejestruj = zarejestrujZapisPrzedWyjsciem(async () => {
      const {
        tpl: teraz, putSettings: zapisz, baselineJson: baza, waznoscTekst: tekst,
      } = wyjscieRef.current;
      const szkic = zWaznosciaPrzed(teraz, tekst, waznoscPrzed.current);
      if (JSON.stringify(szkic) === baza || !settingsRef.current || kontoWKarcie() !== kontoPrzyWejsciu) return;
      zachowajSzkicLokalnie(szkic);
      if (zaDlugiTekstOferty(szkic)) return;
      await zapisz({ offer_template_state: { draft: szkic } });
      usunSzkicLokalny();
      // Ten szkic jest już na serwerze: gdyby po wylogowaniu doszło jednak do odmontowania,
      // sprzątanie niżej nie wyśle go drugi raz, a przeglądarka nie zapyta o wyjście.
      savedDraft.current = JSON.stringify(szkic);
      wyjscieRef.current = { ...wyjscieRef.current, baselineJson: savedDraft.current, dirty: false };
    });
    return () => {
      window.removeEventListener('beforeunload', ostrzez);
      wyrejestruj();
      if (kontoWKarcie() !== kontoPrzyWejsciu) return;
      const {
        tpl: teraz, putSettings: zapisz, baselineJson: baza, waznoscTekst: tekst,
        toast: komunikat, t: tlumacz, navigate: przejdz,
      } = wyjscieRef.current;
      // Wyjście w trakcie wpisywania ważności (np. wstecz w przeglądarce bez opuszczenia pola):
      // niepoprawny tekst liczy się tak jak po wyjściu z pola.
      const szkic = zWaznosciaPrzed(teraz, tekst, waznoscPrzed.current);
      if (JSON.stringify(szkic) === baza) return;
      if (!settingsRef.current) {
        // Ustawienia się nie wczytały: szkicu nie ma na czym oprzeć (przywrócenie nadpisałoby
        // prawdziwy szablon wartościami domyślnymi), więc tylko mówimy prawdę.
        komunikat.warning(tlumacz('offerTemplate.exitNotLoaded'), { duration: 15000 });
        return;
      }
      zachowajSzkicLokalnie(szkic);
      const wroc = { label: tlumacz('offerTemplate.exitBack'), onClick: () => przejdz('/offer-template') };
      if (zaDlugiTekstOferty(szkic)) {
        komunikat.error(tlumacz('offerTemplate.exitTooLong'), { duration: 15000, action: wroc });
        return;
      }
      zapisz({ offer_template_state: { draft: szkic } })
        .then(() => usunSzkicLokalny())
        .catch((e) => {
          komunikat.error(
            tlumacz('offerTemplate.exitSaveFailed').replace('{msg}', (e && e.message) || tlumacz('offerTemplate.saveError')),
            { duration: 15000, action: wroc }
          );
        });
    };
  }, []);

  // Zwraca { ok, error }: okno publikacji czeka na wynik i zamyka się dopiero po sukcesie, a błąd
  // pokazuje w sobie (jak konstruktor umowy, scalenie poprawek 11.10.2026).
  const handlePublish = async () => {
    if (zaDlugiTekstOferty(tpl)) {
      return { ok: false, error: t('offerTemplate.textTooLong') };
    }
    setActionLoading('publish');
    setAlert(null);
    try {
      // Do historii trafia wersja, która WŁAŚNIE przestaje być aktualna — dokładnie to
      // obiecuje okno potwierdzenia („obecna wersja zostanie przeniesiona do historii").
      const nextNo = versions.reduce((max, v) => Math.max(max, Number(v.versionNo) || 0), 0) + 1;
      const nextVersions = publishedTpl
        ? [...versions, { versionNo: nextNo, publishedAt: new Date().toISOString(), template: publishedTpl }].slice(-10)
        : versions;

      await putSettings({
        offer_template: tpl,
        offer_title: tpl.title || '',
        offer_template_state: { draft: null, versions: nextVersions },
      });

      setPublishedTpl(tpl);
      setVersions(nextVersions);
      savedDraft.current = null;
      zapisanoNaSerwerze();
      setAlert({ ok: true, msg: t('offerTemplate.published') });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message || t('offerTemplate.publishError') };
    } finally {
      setActionLoading(null);
    }
  };

  const otworzOknoPublikacji = () => {
    setPublishError(null);
    setShowPublishConfirm(true);
  };
  // W trakcie publikacji okno nie znika: zamknięcie sugerowałoby, że ją odwołano.
  const zamknijOknoPublikacji = () => {
    if (actionLoading === 'publish') return;
    setShowPublishConfirm(false);
  };

  const handleRestoreVersion = async (ver) => {
    // Przywrócenie podmienia treść edytora; niezapisane zmiany giną, więc najpierw pytamy.
    if (dirty) {
      const ok = await confirm({
        title: t('contractBuilder.versionLoadTitle').replace('{n}', String(ver.versionNo)),
        message: t('contractBuilder.versionLoadBody'),
      });
      if (!ok) return;
    }
    setTpl({ ...OFFER_TPL_DEFAULTS, ...(ver.template || {}) });
    setShowVersionHistory(false);
    setAlert({ ok: true, msg: t('offerTemplate.versionRestored').replace('{n}', String(ver.versionNo)) });
  };

  const handleDeleteVersion = async (ver) => {
    // Usunięcie jest trwałe, więc z potwierdzeniem. Szkicu nie wysyłamy wcale: serwer zmienia
    // wtedy tylko historię (12.10.2026). Dawniej szły bieżące, niezapisane zmiany edytora, czyli
    // „Usuń” po cichu zapisywał szkic (test odbiorczy 11.10.2026), a potem szkic z chwili otwarcia
    // karty, który cofał szkic zapisany w innej karcie.
    const ok = await confirm({
      title: t('contractBuilder.versionDeleteTitle').replace('{n}', String(ver.versionNo)),
      message: t('contractBuilder.irreversible'),
      tone: 'danger',
    });
    if (!ok) return;
    const nextVersions = versions.filter(v => v.versionNo !== ver.versionNo);
    try {
      await putSettings({ offer_template_state: { versions: nextVersions } });
      setVersions(nextVersions);
    } catch (e) {
      setAlert({ ok: false, msg: e.message || t('offerTemplate.deleteVersionFailed') });
    }
  };

  /**
   * PDF ze SZKICU, nie z wersji opublikowanej — dlatego osobna trasa
   * `POST /api/preview-offer-pdf` (odpowiednik `/api/preview-contract-pdf`). Generator ofert
   * czyta wyłącznie `offer_template`, więc bez niej podgląd pokazywałby poprzedni dokument.
   */
  const requestPdf = async () => {
    // Serwer odmawia podglądu tekstu, którego nie da się zapisać (ten sam limit co zapis).
    if (zaDlugiTekstOferty(tpl)) throw new Error(t('offerTemplate.textTooLong'));
    const csrf = localStorage.getItem('csrfToken') || '';
    const res = await fetch(`${API_BASE}/api/preview-offer-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
      body: JSON.stringify({ template: tpl }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error((err && err.code === 'offer_text_too_long') ? t('offerTemplate.textTooLong') : t('offerTemplate.pdfFailed'));
    }
    return res.blob();
  };

  const handleViewPdf = async () => {
    setActionLoading('view-pdf');
    setAlert(null);
    try {
      const blob = await requestPdf();
      setPdfPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      setAlert({ ok: false, msg: e.message || t('offerTemplate.previewError') });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    setActionLoading('download');
    setAlert(null);
    try {
      const blob = await requestPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'oferta-testowa.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setAlert({ ok: false, msg: e.message || t('offerTemplate.generateError') });
    } finally {
      setActionLoading(null);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
  };

  const labelFor = useCallback((name) => (OFFER_VAR_KEYS[name] ? t(OFFER_VAR_KEYS[name]) : name), [t]);

  const hasLogo = !!(settings && settings.company_logo);

  // Powody, dla których serwer ODRZUCI publikację: blokują przycisk „Tak, publikuj”. Dawniej okno
  // mówiło „Brak ostrzeżeń”, a publikacja kończyła się błędem (test odbiorczy 11.10.2026).
  const bledyPublikacji = [];
  if (zaDlugiTekstOferty(tpl)) bledyPublikacji.push(t('offerTemplate.textTooLong'));
  if (!settings) bledyPublikacji.push(t('offerTemplate.settingsNotLoaded'));

  // Lista rzeczy do sprawdzenia przed publikacją — odpowiednik checklisty z konstruktora umowy.
  const publishChecklist = [];
  if (!String(tpl.title || '').trim()) publishChecklist.push(t('offerTemplate.warnNoTitle'));
  if (tpl.logoEnabled && !hasLogo) publishChecklist.push(t('offerTemplate.warnNoLogo'));
  // Braki liczymy dopiero po udanym odczycie ustawień: bez niego nie wiadomo, czego brakuje.
  const brakiFirmy = settings ? brakiDanychFirmyOferty(settings).map((b) => t(b.labelKey)).join(', ') : '';
  if (brakiFirmy) publishChecklist.push(t('offerTemplate.warnCompanyData').replace('{fields}', brakiFirmy));

  if (loading) {
    return (
      <Container>
        <TopBar>
          <TitleBlock><PageTitle>{t('offerTemplate.title')}</PageTitle></TitleBlock>
        </TopBar>
        <Intro>{t('offerTemplate.loading')}</Intro>
      </Container>
    );
  }

  // Nieudany odczyt to osobny stan: dawniej odznaka mówiła „Brak szablonu”, jakby firma go nie
  // miała, a „Zapisz szkic” odpowiadał, że ustawienia „jeszcze” się wczytują (test odbiorczy
  // 11.10.2026). Zapis jest wtedy zablokowany, bo nie ma czego bezpiecznie nadpisać.
  const statusTone = odczytNieudany ? 'danger' : templateStatus === 'published' ? 'success' : templateStatus === 'draft' ? 'warning' : 'info';
  const statusLabel = odczytNieudany
    ? t('offerTemplate.statusLoadFailed')
    : templateStatus === 'published' ? t('offerTemplate.statusPublished') : templateStatus === 'draft' ? t('offerTemplate.statusDraft') : t('offerTemplate.statusDefault');
  const statusDescription = odczytNieudany
    ? t('offerTemplate.statusDescLoadFailed')
    : templateStatus === 'published'
      ? t('offerTemplate.statusDescPublished')
      : templateStatus === 'draft'
        ? t('offerTemplate.statusDescDraft')
        : t('offerTemplate.statusDescDefault');

  const vals = SAMPLE(tpl, settings || {});
  // Lustro lib/offerHtml.js: „ważna 1 dzień”, każda inna liczba „dni” (test odbiorczy 11.10.2026).
  const dniWaznosci = Number(tpl.validityDays) || 14;
  // Komunikat przy polu ważności (test odbiorczy 11.10.2026): tekstu nie da się przyjąć (puste, 0,
  // ułamek, ujemne) albo liczba ponad 365, która zostanie przycięta. Dawniej pole pokazywało
  // 999 albo 0 bez słowa, a szablon miał inną wartość.
  const waznoscBlad = waznoscTekst !== null
    && (poprawnaWaznosc(waznoscTekst) === null || Number(waznoscTekst) > 365);

  return (
    <Container>
      {/* Pasek akcji zostaje na ekranie przy przewijaniu — zapis dotyczy calego szablonu,
          a nie tej grupy pol, przy ktorej akurat jestes. */}
      <TopBar>
        <TitleBlock>
          <PageTitle>{t('offerTemplate.title')}</PageTitle>
          <Badge $tone={statusTone}>{statusLabel}</Badge>
          {dirty && <Badge $tone="warning">{t('offerTemplate.unsaved')}</Badge>}
        </TitleBlock>

        <TylkoOdczytWPrzerwie>
        <TopActions>
          <Button onClick={handleSaveDraft} disabled={actionLoading !== null || odczytNieudany}>
            <SaveIcon size={16} /> {actionLoading === 'save-draft' ? t('common.saving') : t('offerTemplate.saveDraft')}
          </Button>
          <PublishButton onClick={otworzOknoPublikacji} disabled={actionLoading !== null || odczytNieudany}>
            <UploadIcon size={16} /> {actionLoading === 'publish' ? t('offerTemplate.publishing') : t('offerTemplate.publish')}
          </PublishButton>
        </TopActions>
        </TylkoOdczytWPrzerwie>
      </TopBar>

      <Intro>
        {t('offerTemplate.intro')}
      </Intro>

      <StatusStrip>
        <StatusText>{statusDescription}</StatusText>
        {versions.length > 0 && (
          <SmallButton onClick={() => setShowVersionHistory(v => !v)}>
            {t('offerTemplate.history').replace('{count}', String(versions.length))}
          </SmallButton>
        )}
        <SmallButton onClick={() => navigate('/contract-settings')}>
          {t('offerTemplate.contractSettings')}
        </SmallButton>
      </StatusStrip>

      <Notices>
        {alert && (
          <Banner tone={alert.ok ? 'success' : 'danger'}>{alert.msg}</Banner>
        )}

        {odczytNieudany && (
          <Banner
            tone="danger"
            title={t('offerTemplate.loadFailed')}
            data-cy="offer-load-failed"
            action={{ label: t('offerTemplate.reload'), onClick: () => window.location.reload() }}
          >
            {t('offerTemplate.loadFailedBody')}
          </Banner>
        )}

        {brakiFirmy && (
          <Banner
            tone="warning"
            title={t('offerTemplate.companyIncomplete')}
            data-cy="offer-company-incomplete"
            action={{ label: t('offerTemplate.fillCompany'), onClick: () => navigate('/contract-settings') }}
          >
            {t('offerTemplate.companyIncompleteBody').replace('{fields}', brakiFirmy)}
          </Banner>
        )}

        {przywrocono && (
          <Banner
            tone="warning"
            title={t('offerTemplate.restoredTitle')}
            data-cy="offer-restored-draft"
            action={{ label: t('offerTemplate.restoredDiscard'), onClick: odrzucPrzywrocone }}
          >
            {t('offerTemplate.restoredBody')}
          </Banner>
        )}
      </Notices>

      {showVersionHistory && versions.length > 0 && (
        <HistoryBox>
          <HistoryHead>
            <span>{t('offerTemplate.historyHead').replace('{count}', String(versions.length))}</span>
            <SmallButton onClick={() => setShowVersionHistory(false)}>{t('common.close')}</SmallButton>
          </HistoryHead>
          {versions.slice().reverse().map((ver) => (
            <VersionCard key={ver.versionNo}>
              <div>
                <strong>v{ver.versionNo}</strong>
                {ver.publishedAt && (
                  <VersionDate>{new Date(ver.publishedAt).toLocaleString('pl-PL')}</VersionDate>
                )}
              </div>
              <TylkoOdczytWPrzerwie>
              <ChipRow>
                <SmallButton onClick={() => handleRestoreVersion(ver)} title={t('offerTemplate.restoreTitle')}>
                  {t('offerTemplate.restore')}
                </SmallButton>
                <SmallButton
                  onClick={() => handleDeleteVersion(ver)}
                  style={{ color: 'var(--danger)' }}
                  title={t('offerTemplate.deleteVersionTitle')}
                >
                  {t('common.delete')}
                </SmallButton>
              </ChipRow>
              </TylkoOdczytWPrzerwie>
            </VersionCard>
          ))}
        </HistoryBox>
      )}

      <MainGrid>
        {/* ── PANEL EDYCJI ── */}
        <TylkoOdczytWPrzerwie>
        <Panel>
          <PanelHead>
            <PanelTitle>{t('offerTemplate.contentPanel')}</PanelTitle>
          </PanelHead>

          <Group>
            <GroupTitle>{t('offerTemplate.groupHeader')}</GroupTitle>
            <Field>
              <Label htmlFor="offer-title">{t('offerTemplate.offerTitle')}</Label>
              <Input
                id="offer-title"
                value={tpl.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder={OFFER_TPL_DEFAULTS.title}
                maxLength={200}
              />
            </Field>

            <Field>
              <Check>
                <input
                  type="checkbox"
                  checked={!!tpl.logoEnabled}
                  onChange={(e) => setField('logoEnabled', e.target.checked)}
                />
                {t('offerTemplate.showLogo')}
              </Check>
              {tpl.logoEnabled && !hasLogo && (
                <Hint>
                  {t('offerTemplate.noLogoHint')}{' '}
                  <LinkButton onClick={() => navigate('/contract-settings')} data-cy="offer-logo-company-link">
                    {t('offerTemplate.fillCompany')}
                  </LinkButton>
                </Hint>
              )}
              {tpl.logoEnabled && (
                <Row style={{ marginTop: 'var(--sp-3)' }}>
                  <Label as="span" style={{ marginBottom: 0 }}>{t('offerTemplate.logoAlign')}</Label>
                  <SimpleSelect
                    style={{ width: 160 }}
                    aria-label={t('offerTemplate.logoAlign')}
                    value={tpl.logoAlign}
                    onChange={(val) => setField('logoAlign', val)}
                    options={[
                      { value: 'left', label: t('offerTemplate.alignLeft') },
                      { value: 'center', label: t('offerTemplate.alignCenter') },
                      { value: 'right', label: t('offerTemplate.alignRight') },
                    ]}
                  />
                </Row>
              )}
            </Field>

            <Field>
              <Row>
                <Label as="span" style={{ marginBottom: 0 }}>{t('offerTemplate.fontSize')}</Label>
                <SimpleSelect
                  style={{ width: 100 }}
                  aria-label={t('offerTemplate.fontSize')}
                  value={tpl.fontPt}
                  onChange={(val) => setField('fontPt', parseInt(val, 10))}
                  options={[9, 10, 11, 12, 13, 14].map(pt => ({ value: pt, label: `${pt} pt` }))}
                />
              </Row>
            </Field>
          </Group>

          <Group>
            <GroupTitle>{t('offerTemplate.groupTexts')}</GroupTitle>

            <Field>
              <Label as="span">{t('offerTemplate.varsLabel')}</Label>
              <ChipRow data-tour="offer-fields">
                {Object.keys(OFFER_VAR_KEYS).map((name) => (
                  <VariableChip
                    key={name}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertVar(name)}
                  >
                    {labelFor(name)}
                  </VariableChip>
                ))}
              </ChipRow>
              <Hint>{t('offerTemplate.varsHint')}</Hint>
            </Field>

            <Field>
              <Label as="span">{t('offerTemplate.introLabel')}</Label>
              <EmailPillField
                value={tpl.introText}
                ariaLabel={t('offerTemplate.introLabel')}
                labelFor={labelFor}
                onChange={(v) => setField('introText', v)}
                onFocusEditor={(ed) => { activeEditor.current = ed; }}
              />
              <LicznikZnakow tekst={tpl.introText} dataCy="offer-intro-count" />
            </Field>

            <Field>
              <Label as="span">{t('offerTemplate.footerLabel')}</Label>
              <EmailPillField
                value={tpl.footerText}
                ariaLabel={t('offerTemplate.footerLabel')}
                labelFor={labelFor}
                onChange={(v) => setField('footerText', v)}
                onFocusEditor={(ed) => { activeEditor.current = ed; }}
              />
              <LicznikZnakow tekst={tpl.footerText} dataCy="offer-footer-count" />
            </Field>
          </Group>

          <Group>
            <GroupTitle>{t('offerTemplate.groupValidity')}</GroupTitle>
            <Field>
              <Row>
                {/* Prawdziwy <label>: `as="span"` z htmlFor niczego nie wiązał, więc pole nie
                    miało nazwy dostępnej (test odbiorczy 11.10.2026). */}
                <Label htmlFor="offer-validity" style={{ marginBottom: 0 }}>{t('offerTemplate.validityLabel')}</Label>
                <Input
                  id="offer-validity"
                  type="number"
                  min={1}
                  max={365}
                  value={waznoscTekst ?? String(tpl.validityDays ?? 14)}
                  aria-invalid={waznoscBlad ? 'true' : undefined}
                  data-error={waznoscBlad ? 'true' : undefined}
                  aria-describedby={waznoscBlad ? 'offer-validity-error offer-validity-hint' : 'offer-validity-hint'}
                  onFocus={() => { waznoscPrzed.current = tpl.validityDays ?? 14; }}
                  onChange={(e) => {
                    const tekst = e.target.value;
                    setWaznoscTekst(tekst);
                    const dni = poprawnaWaznosc(tekst);
                    if (dni !== null) setField('validityDays', dni);
                  }}
                  onBlur={() => {
                    // Niepoprawny tekst po wyjściu z pola: wraca liczba sprzed edycji.
                    if (waznoscTekst !== null && poprawnaWaznosc(waznoscTekst) === null) {
                      setField('validityDays', waznoscPrzed.current ?? 14);
                    }
                    setWaznoscTekst(null);
                  }}
                  style={{ maxWidth: 120 }}
                />
              </Row>
              {waznoscBlad && (
                <Hint id="offer-validity-error" data-cy="offer-validity-error" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                  {poprawnaWaznosc(waznoscTekst) === null
                    ? t('offerTemplate.validityInvalid').replace('{n}', String(waznoscPrzed.current ?? 14))
                    : t('offerTemplate.validityTooLong')}
                </Hint>
              )}
              <Hint id="offer-validity-hint">{t('offerTemplate.validityHint')}</Hint>
            </Field>
          </Group>
        </Panel>
        </TylkoOdczytWPrzerwie>

        {/* ── PANEL PODGLĄDU ── */}
        <PreviewPanel>
          <PanelHead data-tour="offer-preview">
            <PanelTitle>{t('offerTemplate.previewPanel')}</PanelTitle>
            <Badge $tone="warning">{t('offerTemplate.sampleData')}</Badge>
            {/* Obie akcje PDF stoją przy podglądzie, bo działają właśnie na nim —
                identycznie jak w konstruktorze umowy. */}
            <PanelTools>
              <Button ref={pdfPrzyciskRef} onClick={handleViewPdf} disabled={actionLoading !== null}>
                <EyeIcon size={16} /> {actionLoading === 'view-pdf' ? t('offerTemplate.generating') : t('offerTemplate.pdfPreview')}
              </Button>
              <Button onClick={handleDownloadPdf} disabled={actionLoading !== null}>
                <DownloadIcon size={16} /> {actionLoading === 'download' ? t('offerTemplate.downloading') : t('offerTemplate.downloadPdf')}
              </Button>
            </PanelTools>
          </PanelHead>

          <PreviewFrame>
            {/* Marginesy MUSZĄ odpowiadać wywołaniom page.pdf w routes/offers.js
                (15/15/16/15 mm) — inaczej kartka w podglądzie łamie wiersze w innym
                miejscu niż wydruk. */}
            {/* pageFooter: PDF oferty ma na każdej stronie stopkę z nazwą firmy i numerem strony
                (routes/offers.js, buildPdfFooterTemplate), więc kartka też (test odbiorczy 11.10.2026). */}
            <PaperSheet
              marginTopMm={15}
              marginSideMm={15}
              marginBottomMm={16}
              onPagesChange={setPreviewPages}
              pageFooter={String((settings && settings.company_name) || '').trim()}
            >
              {/* P8.1e (5/6): etykiety WEWNĄTRZ tej kartki zostają po polsku i nie jest to
                  przeoczenie. Blok jest LUSTREM `Login/backend/lib/offerHtml.js` — sprawdzone
                  co do napisu: „DATA OFERTY", „NUMER OFERTY", „RAZEM DO ZAPŁATY",
                  „DEPOZYT ZWROTNY", „Dane do przelewu", „cena/dzień", „Oferta jest ważna"
                  stoją tam tak samo. Backend renderuje PDF po polsku niezależnie od języka
                  panelu, więc przetłumaczony podgląd pokazywałby dokument, którego klient
                  nigdy nie dostanie — a wierność jest jedynym zadaniem podglądu. Tłumaczyć
                  można dopiero RAZEM z `lib/offerHtml.js`. Chrom dookoła (pasek, przyciski,
                  odznaka „przykładowe dane") jest przetłumaczony. */}
              <Paper $fontPt={tpl.fontPt}>
                {tpl.logoEnabled && hasLogo && (
                  <PLogo $align={tpl.logoAlign}><img src={settings.company_logo} alt="Logo firmy" /></PLogo>
                )}
                <PTitle>{tpl.title || OFFER_TPL_DEFAULTS.title}</PTitle>
                <PTitleRule />
                {tpl.introText && <PIntro>{renderSampleText(tpl.introText, vals)}</PIntro>}

                <PInfoTable>
                  <tbody>
                    <tr><td className="label">DATA OFERTY</td><td className="val">{vals.dataOferty}</td></tr>
                    <tr><td className="label">NUMER OFERTY</td><td className="val">{vals.nrOferty}</td></tr>
                    <tr><td className="label">IMIĘ I NAZWISKO KLIENTA</td><td className="val">{vals.klient}</td></tr>
                    <tr><td className="label">EMAIL</td><td className="val">{vals.email}</td></tr>
                  </tbody>
                </PInfoTable>

                {/* Wiersz terminu jest w generowanym PDF od dawna, ale podgląd go pomijał —
                    klient nie widział jednego z bardziej rzucających się w oczy bloków oferty. */}
                <PDateRow>
                  <tbody>
                    <tr>
                      <td className="caption" style={{ width: '20%' }}>Termin<br />wypożyczenia</td>
                      <td className="when" style={{ width: '15%' }}>od:</td>
                      <td className="stamp" style={{ width: '25%' }}>{vals.terminOd}</td>
                      <td className="when" style={{ width: '15%' }}>do:</td>
                      <td className="stamp" style={{ width: '25%' }}>{vals.terminDo}</td>
                    </tr>
                  </tbody>
                </PDateRow>

                <PEquip>
                  <thead>
                    {/* Nagłówki i szerokości idą z utils/offerTableColumns.js, bo podgląd jest
                        lustrem generatora PDF (Login/backend/lib/offerHtml.js). Nie wpisywać ich
                        tu z powrotem: zapadka po stronie backendu pilnuje właśnie tamtej listy. */}
                    <tr>
                      {OFFER_TABLE_COLUMNS.map((k) => (
                        <th key={k.label} style={{ width: k.width }}>{k.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PRZYKLADOWE_WIERSZE.map((wiersz) => (
                      <tr key={wiersz[0]}>
                        {wiersz.map((komorka, i) => (
                          <td key={i} style={{ textAlign: OFFER_TABLE_CELL_ALIGN[i] || 'left' }}>{komorka}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </PEquip>

                <PSummary>
                  <tbody>
                    <tr><td className="label">CENA</td><td className="val">{vals.cena} zł</td></tr>
                    <tr><td className="label">RABAT</td><td className="val">{vals.rabat} zł</td></tr>
                    <tr className="total"><td className="label">RAZEM DO ZAPŁATY</td><td className="val">{vals.razem} zł</td></tr>
                    <tr><td className="label">ZALICZKA</td><td className="val">{vals.zaliczka} zł</td></tr>
                    <tr><td className="label">DEPOZYT ZWROTNY</td><td className="val">{vals.depozyt} zł</td></tr>
                  </tbody>
                </PSummary>

                <PPayBox>
                  <div className="pay-title">Dane do przelewu</div>
                  {vals.firma}<br />{vals.bank}<br />{vals.nrKonta}<br />
                  Tytuł przelewu: {vals.klient}, nr oferty {vals.nrOferty}
                </PPayBox>

                {tpl.footerText && <PFooter>{renderSampleText(tpl.footerText, vals)}</PFooter>}
                <PValidity>Oferta jest ważna {dniWaznosci} {dniWaznosci === 1 ? 'dzień' : 'dni'} od daty wystawienia ({'do '}{waznaDoPodglad(dniWaznosci)})</PValidity>
              </Paper>
            </PaperSheet>
          </PreviewFrame>
          <SheetNote>
            A4 · {pageCountLabel(previewPages)}
            {previewPages > 1 && ` · ${t('offerTemplate.pagesApprox')}`}
          </SheetNote>
        </PreviewPanel>
      </MainGrid>

      {showPublishConfirm && (
        <ModalOverlay onClick={zamknijOknoPublikacji}>
          <ModalBox onClick={(e) => e.stopPropagation()} closeLabel={t('common.close')}>
            <ModalTitle>{t('offerTemplate.publishConfirmTitle')}</ModalTitle>
            <ModalText>
              {t('offerTemplate.publishConfirmBody')}
              {publishedTpl && ` ${t('offerTemplate.publishMovesToHistory')}`}
            </ModalText>
            {bledyPublikacji.length > 0 && (
              <Banner tone="danger" title={t('offerTemplate.publishBlocked')} data-cy="offer-publish-blocked">
                <ul style={{ margin: 'var(--sp-2) 0 0 0', paddingLeft: 18 }}>
                  {bledyPublikacji.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </Banner>
            )}
            {publishChecklist.length === 0 ? (
              bledyPublikacji.length === 0 && <Banner tone="success">{t('offerTemplate.noWarnings')}</Banner>
            ) : (
              <Banner tone="warning" title={t('offerTemplate.checkBeforePublish')}>
                <ul style={{ margin: 'var(--sp-2) 0 0 0', paddingLeft: 18 }}>
                  {publishChecklist.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </Banner>
            )}
            {publishError && (
              <div style={{ marginTop: 'var(--sp-2)' }} data-cy="offer-publish-error">
                <Banner tone="danger">{publishError}</Banner>
              </div>
            )}
            <ModalActions>
              <Button onClick={zamknijOknoPublikacji} disabled={actionLoading === 'publish'}>{t('common.cancel')}</Button>
              <PublishButton
                disabled={bledyPublikacji.length > 0 || actionLoading !== null}
                onClick={async () => {
                  setPublishError(null);
                  const wynik = await handlePublish();
                  if (wynik && wynik.ok) setShowPublishConfirm(false);
                  else if (wynik && wynik.error) setPublishError(wynik.error);
                }}
              >
                {actionLoading === 'publish' ? t('offerTemplate.publishing') : t('offerTemplate.yesPublish')}
              </PublishButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {pdfPreviewUrl && (
        <ModalOverlay onClick={closePdfPreview} powrotFokusu={pdfPrzyciskRef}>
          <PdfModalBox onClick={(e) => e.stopPropagation()}>
            <PdfModalHead>
              <span>{t('offerTemplate.pdfModalHint')}</span>
              <ChipRow>
                <PdfDownloadLink href={pdfPreviewUrl} download="oferta-testowa.pdf">
                  {t('common.download')}
                </PdfDownloadLink>
                <PdfCloseButton onClick={closePdfPreview} />
              </ChipRow>
            </PdfModalHead>
            <PdfFrame title={t('offerTemplate.pdfFrameTitle')} src={pdfPreviewUrl} />
          </PdfModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default OfferTemplate;
